import AddRounded from '@mui/icons-material/AddRounded';
import { ButtonLink } from '@src/common/components/button/ButtonLink';
import type { Row } from '@src/common/components/DraggableTable';
import { Stack } from '@src/common/components/Stack';
import { StatusBadge } from '@src/common/components/StatusBadge';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { PermitList } from '@src/orders/components/permitList/PermitList';
import type { PermitSummaryGroup } from '@src/orders/domain/OrderList';
import { OrderActions, OrderListing } from '@src/orders/domain/OrderList';
import { useChangePermitsIndexing } from '@src/orders/hooks/useChangePermitsIndexing';
import { OrdersNavigation } from '@src/routing/OrdersNavigation';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

type PermitSummariesProps = {
  order: OrderListing;
  onChange: (order: OrderListing) => void;
  onAssign: () => void;
  highlightedPermit?: string;
};

export const PermitSummaries = ({
  order,
  onChange,
  onAssign,
  highlightedPermit,
}: PermitSummariesProps) => {
  const { t } = useTranslation();
  const client = useQueryClient();

  const { changePermitsIndexing } = useChangePermitsIndexing({
    orderId: order.id,
  });

  const reorderPermits = (updatedRows: Row[]) => {
    const newIndexing = updatedRows.flatMap((row) => {
      const subRowIds = row.subRows?.map((subRow) => subRow.id) ?? [];
      return [row.id, ...subRowIds];
    });
    changePermitsIndexing({ data: { indexing: newIndexing } });
    changeSummaryOrdering(order, onChange, newIndexing, client);
  };

  return (
    <Stack gap={theme.spacing['3']} marginTop={theme.spacing['12']}>
      <Stack direction="row" justifyContent="space-between">
        <Stack direction="row" gap={theme.spacing['2']}>
          <Typography variant="h5">{t('permits')}</Typography>
          <StatusBadge
            size="medium"
            text={t('permitSummaries.application', {
              count: order.getAllPermits().length,
            })}
            color="#344054"
            borderColor="#E4E7EC"
            backgroundColor="#F9FAFB"
            radiusFull
          />
        </Stack>
        {order.canDo(OrderActions.ADD_PERMIT) && (
          <ButtonLink
            to={OrdersNavigation.addPermits.url(order.id)}
            startIcon={<AddRounded />}
            variant="contained"
          >
            {t('permits')}
          </ButtonLink>
        )}
      </Stack>

      <PermitList
        order={order}
        onAssign={onAssign}
        onDrag={reorderPermits}
        highlightedPermit={highlightedPermit}
      />
    </Stack>
  );
};

const changeSummaryOrdering = (
  order: OrderListing,
  onChange: (order: OrderListing) => void,
  newIndexing: string[],
  client: QueryClient,
) => {
  const summariesMap = new Map(
    order.permitSummaryGroups.map((group) => [group.originalPermit.id, group]),
  );

  const newSummaryGroupOrder = newIndexing
    .map((applicationId) => {
      const summary = summariesMap.get(applicationId);

      if (!summary) return undefined;

      return summary;
    })
    .filter((summary): summary is PermitSummaryGroup => summary !== undefined);

  client.setQueryData(
    ['order.listing', order.id],
    (prev: OrderListing | undefined) => {
      if (!prev) return prev;
      return new OrderListing({
        ...prev,
        permitSummaryGroups: newSummaryGroupOrder,
      });
    },
  );

  onChange(
    new OrderListing({ ...order, permitSummaryGroups: newSummaryGroupOrder }),
  );
};
